import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const isBrowser = typeof window !== "undefined";
const THEME_STORAGE_KEY = "elitlig.altHomeTheme";
const PREFERS_DARK_QUERY = "(prefers-color-scheme: dark)";

const readInitialTheme = (prefRef) => {
  if (!isBrowser) {
    prefRef.current = "system";
    return false;
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark") {
      prefRef.current = "user";
      return true;
    }
    if (stored === "light") {
      prefRef.current = "user";
      return false;
    }

    if (window.matchMedia?.(PREFERS_DARK_QUERY)) {
      const media = window.matchMedia(PREFERS_DARK_QUERY);
      prefRef.current = "system";
      return media.matches;
    }
  } catch {
    // ignore read errors
  }

  prefRef.current = "system";
  return false;
};

export function useAltTheme() {
  const preferenceRef = useRef("system");
  const [isDarkMode, setIsDarkMode] = useState(() => readInitialTheme(preferenceRef));

  useEffect(() => {
    if (!isBrowser) return;
    try {
      if (preferenceRef.current === "user") {
        window.localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? "dark" : "light");
      } else {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
      }
    } catch {
      // ignore write errors
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (!isBrowser || !window.matchMedia) return;
    const media = window.matchMedia(PREFERS_DARK_QUERY);
    const handleChange = (event) => {
      if (preferenceRef.current !== "user") {
        preferenceRef.current = "system";
        setIsDarkMode(event.matches);
      }
    };

    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
    } else if (media.addListener) {
      media.addListener(handleChange);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", handleChange);
      } else if (media.removeListener) {
        media.removeListener(handleChange);
      }
    };
  }, []);

  const toggleTheme = useCallback(() => {
    preferenceRef.current = "user";
    setIsDarkMode((prev) => !prev);
  }, []);

  const resetToSystem = useCallback(() => {
    preferenceRef.current = "system";
    if (!isBrowser) {
      setIsDarkMode(false);
      return;
    }

    try {
      if (window.matchMedia?.(PREFERS_DARK_QUERY)) {
        const media = window.matchMedia(PREFERS_DARK_QUERY);
        setIsDarkMode(media.matches);
      } else {
        setIsDarkMode(false);
      }
    } catch {
      setIsDarkMode(false);
    }
  }, []);

  const themeClassName = useMemo(
    () => `alt-home-page${isDarkMode ? " is-dark" : ""}`,
    [isDarkMode]
  );

  return {
    isDarkMode,
    toggleTheme,
    resetToSystem,
    themeClassName,
  };
}

export function getAltThemeStorageKey() {
  return THEME_STORAGE_KEY;
}
